import React, { useState, useEffect } from 'react';
import config from '../config';
import Helper from '../helpers';

const apiBaseUrl = config.apiBaseUrl;

/**
 * BeaconsReferralWidget
 *
 * Shown to venue admin and PT directory users in the dashboard.
 * - Displays the platform's Beacons.ai sign-up referral link so PTs can create
 *   their own Beacons page (earning the platform owner a referral commission).
 * - Allows the user to save their own Beacons.ai username so it can be shown
 *   on their public PT directory listing.
 */
const BeaconsReferralWidget = ({ isAdmin }) => {
    const [platformUsername, setPlatformUsername] = useState('mrchadwick');
    const [myBeaconsUsername, setMyBeaconsUsername] = useState('');
    const [inputUsername, setInputUsername] = useState('');
    const [hasListing, setHasListing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [saveError, setSaveError] = useState('');

    // Social media URLs
    const [inputSocialLinks, setInputSocialLinks] = useState({ tikTokUrl: '', blueskyUrl: '', instagramUrl: '', facebookUrl: '', stravaUrl: '', linkedInUrl: '' });

    useEffect(() => {
        const init = async () => {
            try {
                // Fetch the platform's referral username
                const res = await fetch(`${apiBaseUrl}/api/platform-settings/beacons-referral-username`);
                if (res.ok) {
                    const data = await res.json();
                    setPlatformUsername(data.username || 'mrchadwick');
                }
            } catch {
                // use default
            }

            try {
                // Fetch the user's own PT directory listing (if any)
                const token = await Helper.getCookie('wh-jwt');
                if (token) {
                    const listingRes = await fetch(`${apiBaseUrl}/api/ptdirectory/my-listing`, {
                        headers: { 'Authorization': `Bearer ${token}` },
                        credentials: 'include'
                    });
                    if (listingRes.ok) {
                        const listingData = await listingRes.json();
                        if (listingData.status === 'success' && listingData.listing) {
                            setHasListing(true);
                            const existing = listingData.listing.beaconsUsername || '';
                            setMyBeaconsUsername(existing);
                            setInputUsername(existing);
                            const links = {
                                tikTokUrl: listingData.listing.tikTokUrl || '',
                                blueskyUrl: listingData.listing.blueskyUrl || '',
                                instagramUrl: listingData.listing.instagramUrl || '',
                                facebookUrl: listingData.listing.facebookUrl || '',
                                stravaUrl: listingData.listing.stravaUrl || '',
                                linkedInUrl: listingData.listing.linkedInUrl || '',
                            };
                            setInputSocialLinks(links);
                        }
                    }
                }
            } catch {
                // no listing – that's fine
            }
        };
        init();
    }, []);

    const referralLink = `https://beacons.ai/signup?c=${platformUsername}`;

    const handleSaveMyUsername = async () => {
        setSaving(true);
        setSaveError('');
        setSaveSuccess(false);
        try {
            const token = await Helper.getCookie('wh-jwt');
            const res = await fetch(`${apiBaseUrl}/api/ptdirectory/my-listing`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    beaconsUsername: inputUsername.trim().replace(/^@/, ''),
                    tikTokUrl: inputSocialLinks.tikTokUrl.trim() || null,
                    blueskyUrl: inputSocialLinks.blueskyUrl.trim() || null,
                    instagramUrl: inputSocialLinks.instagramUrl.trim() || null,
                    facebookUrl: inputSocialLinks.facebookUrl.trim() || null,
                    stravaUrl: inputSocialLinks.stravaUrl.trim() || null,
                    linkedInUrl: inputSocialLinks.linkedInUrl.trim() || null,
                })
            });
            if (res.ok) {
                const data = await res.json();
                setMyBeaconsUsername(data.listing?.beaconsUsername || inputUsername.trim());
                setInputSocialLinks({
                    tikTokUrl: data.listing?.tikTokUrl || '',
                    blueskyUrl: data.listing?.blueskyUrl || '',
                    instagramUrl: data.listing?.instagramUrl || '',
                    facebookUrl: data.listing?.facebookUrl || '',
                    stravaUrl: data.listing?.stravaUrl || '',
                    linkedInUrl: data.listing?.linkedInUrl || '',
                });
                setSaveSuccess(true);
                setTimeout(() => setSaveSuccess(false), 3000);
            } else {
                setSaveError('Failed to save. Please try again.');
            }
        } catch {
            setSaveError('Failed to save. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="card p-4 sm:p-6">
            {/* Header */}
            {/*<div className="flex items-center mb-4">*/}
            {/*    <LinkIcon className="h-5 w-5 text-emerald-400 mr-2" />*/}
            {/*    <h3 className="text-base sm:text-lg font-semibold text-white">Beacons.ai – Sell Your Services</h3>*/}
            {/*</div>*/}

            {/*<p className="text-gray-400 text-sm mb-4">*/}
            {/*    Another string to boost the power of your social bow.{' '}*/}
            {/*    <a*/}
            {/*        href={referralLink}*/}
            {/*        target="_blank"*/}
            {/*        rel="noopener noreferrer"*/}
            {/*        className="text-emerald-400 hover:text-emerald-300 underline"*/}
            {/*    >*/}
            {/*        Beacons.ai*/}
            {/*    </a>{' '}*/}
            {/*    is a free promotional page that sits alongside your WorldsHighStreet profile.*/}
            {/*    Copy the referral link below to sign up, or click straight through:*/}
            {/*</p>*/}

            {/* Referral link row */}
            {/*<div className="flex items-center gap-2 mb-4">*/}
            {/*    <div className="flex-1 bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-sm text-emerald-300 truncate">*/}
            {/*        {referralLink}*/}
            {/*    </div>*/}
            {/*    <button*/}
            {/*        onClick={handleCopyReferralLink}*/}
            {/*        className="flex-shrink-0 flex items-center gap-1 px-3 py-2 bg-emerald-700 hover:bg-emerald-600 text-white rounded-lg text-sm transition-colors"*/}
            {/*        title="Copy referral link"*/}
            {/*    >*/}
            {/*        {copied ? (*/}
            {/*            <><CheckIcon className="h-4 w-4" /><span className="hidden sm:inline">Copied!</span></>*/}
            {/*        ) : (*/}
            {/*            <><DocumentDuplicateIcon className="h-4 w-4" /><span className="hidden sm:inline">Copy</span></>*/}
            {/*        )}*/}
            {/*    </button>*/}
            {/*    <a*/}
            {/*        href={referralLink}*/}
            {/*        target="_blank"*/}
            {/*        rel="noopener noreferrer"*/}
            {/*        className="flex-shrink-0 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors"*/}
            {/*    >*/}
            {/*        Sign Up*/}
            {/*    </a>*/}
            {/*</div>*/}
            
            {/* PT's own Beacons username (only if they have a directory listing) */}
            {hasListing && (
                <div className="pt-4 border-t border-gray-700">
                    <p className="text-sm text-gray-300 mb-2 font-medium">
                        Your Beacons.ai profile
                    </p>
                    
                    <div className="flex items-center gap-2">
                        <span className="text-gray-400 text-sm flex-shrink-0">beacons.ai/</span>
                        <input
                            type="text"
                            value={inputUsername}
                            onChange={e => setInputUsername(e.target.value.replace(/^@/, ''))}
                            className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            placeholder="your-username"
                        />
                        {!inputUsername && (
                            <a
                                href={referralLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-shrink-0 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors"
                            >
                                Sign Up
                            </a>
                        )}
                    </div>

                    {/* Social media links */}
                    <p className="text-sm text-gray-300 mt-4 mb-2 font-medium">
                        Social Media Links
                    </p>
                    <p className="text-xs text-gray-400 mb-3">
                        Add your social media profile URLs to display them on your PT directory page.
                    </p>
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-sm w-20 flex-shrink-0">TikTok</span>
                            <input
                                type="url"
                                value={inputSocialLinks.tikTokUrl}
                                onChange={e => setInputSocialLinks(prev => ({ ...prev, tikTokUrl: e.target.value }))}
                                className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                placeholder="https://www.tiktok.com/@yourusername"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-sm w-20 flex-shrink-0">Bluesky</span>
                            <input
                                type="url"
                                value={inputSocialLinks.blueskyUrl}
                                onChange={e => setInputSocialLinks(prev => ({ ...prev, blueskyUrl: e.target.value }))}
                                className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                placeholder="https://bsky.app/profile/yourusername"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-sm w-20 flex-shrink-0">Instagram</span>
                            <input
                                type="url"
                                value={inputSocialLinks.instagramUrl}
                                onChange={e => setInputSocialLinks(prev => ({ ...prev, instagramUrl: e.target.value }))}
                                className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                placeholder="https://www.instagram.com/yourusername"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-sm w-20 flex-shrink-0">Facebook</span>
                            <input
                                type="url"
                                value={inputSocialLinks.facebookUrl}
                                onChange={e => setInputSocialLinks(prev => ({ ...prev, facebookUrl: e.target.value }))}
                                className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                placeholder="https://www.facebook.com/yourpage"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-sm w-20 flex-shrink-0">Strava</span>
                            <input
                                type="url"
                                value={inputSocialLinks.stravaUrl}
                                onChange={e => setInputSocialLinks(prev => ({ ...prev, stravaUrl: e.target.value }))}
                                className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                placeholder="https://www.strava.com/athletes/yourusername"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-sm w-20 flex-shrink-0">LinkedIn</span>
                            <input
                                type="url"
                                value={inputSocialLinks.linkedInUrl}
                                onChange={e => setInputSocialLinks(prev => ({ ...prev, linkedInUrl: e.target.value }))}
                                className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                placeholder="https://www.linkedin.com/in/yourprofile"
                            />
                        </div>
                    </div>

                    <button
                        onClick={handleSaveMyUsername}
                        disabled={saving}
                        className="mt-4 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                        {saving ? 'Saving…' : 'Save Profile Links'}
                    </button>
                    {saveSuccess && (
                        <p className="text-emerald-400 text-xs mt-2">✓ Profile links saved to your directory listing.</p>
                    )}
                    {saveError && (
                        <p className="text-red-400 text-xs mt-2">{saveError}</p>
                    )}
                    {myBeaconsUsername && (
                        <p className="text-xs text-gray-500 mt-2">
                            Your Beacons profile:{' '}
                            <a
                                href={`https://beacons.ai/${myBeaconsUsername}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-emerald-400 hover:underline"
                            >
                                beacons.ai/{myBeaconsUsername}
                            </a>
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default BeaconsReferralWidget;
